import type { ComponentPropsWithRef, ReactNode } from 'react';
import type { CategoryMeta } from '@/components/transactions/screen/categoryIndex';
import { categoryChipStyleFromMeta } from '@/components/transactions/screen/categoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { amountColor } from '@/lib/transactions/utils';
import { cn } from '@/lib/utils';

// The one transaction line of the app. The transactions list, a fatura's items, a space's expenses and the send flow
// all compose it, so a purchase reads the same wherever it shows up; each caller only decides what goes in the slots.

const CONTAINER = {
  row: 'flex w-full items-center gap-3 border-b border-border-subtle px-5 py-3 text-left',
  compact: 'flex items-center gap-2.5 px-1 py-2',
} as const;

/** Hover/press feedback for a line that is itself the click target (or holds a full-size overlay button). */
export const TRANSACTION_LINE_INTERACTIVE = 'transition-colors hover:bg-black/1.5 active:bg-black/3';

type LineProps = Readonly<{
  /** `button` when the whole line is the trigger (the transactions list's collapsible row). */
  as?: 'div' | 'li' | 'button';
  density?: keyof typeof CONTAINER;
  /** Usually a `CategoryTile`. */
  leading: ReactNode;
  /** Usually a `TransactionTitle`. */
  title: ReactNode;
  /** Usually a `TransactionMeta`; rendered under the title. */
  meta?: ReactNode;
  /** Extra content inside the text column, between the title and the meta (markers, actions). */
  children?: ReactNode;
  /** A column between the text and the amount, for wide layouts only — the caller decides when it shows. */
  aside?: ReactNode;
  /** Usually a `TransactionAmount`. */
  amount: ReactNode;
  /** A caret or an action after the amount. */
  trailing?: ReactNode;
  className?: string;
}> & Omit<ComponentPropsWithRef<'button'>, 'title' | 'children' | 'className'>;

export function TransactionLine({
  as = 'div',
  density = 'row',
  leading,
  title,
  meta,
  children,
  aside,
  amount,
  trailing,
  className,
  ...rest
}: LineProps) {
  // Typed as the widest of the three: a div or li only ever receives the shared HTML attributes.
  const Element = as as 'button';
  const buttonType = as === 'button' ? { type: 'button' as const } : {};
  return (
    <Element {...buttonType} {...rest} className={cn(CONTAINER[density], className)}>
      {leading}
      <div className="min-w-0 flex-1">
        {title}
        {children}
        {meta}
      </div>
      {aside}
      {amount}
      {trailing}
    </Element>
  );
}

/** The line's name, with room for small marks after it (renamed pencil, installment, space pills). */
export function TransactionTitle({ children, adornments }: Readonly<{ children: ReactNode; adornments?: ReactNode }>) {
  return (
    <div className="flex items-center gap-1.5">
      <p className="min-w-0 flex-1 line-clamp-2 text-sm leading-tight font-medium wrap-break-word text-text-primary">{children}</p>
      {adornments}
    </div>
  );
}

/**
 * The metadata under the title. On a phone each item takes its own line (a long category is never cut by what
 * follows it); from `sm:` they collapse into one dotted line.
 */
export function TransactionMeta({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <div className="mt-0.5 flex flex-col gap-1 text-2xs text-text-secondary sm:flex-row sm:items-center sm:gap-1.5">
      {children}
    </div>
  );
}

/** One item of `TransactionMeta`; `separated` draws the "·" before it on the single-line layout. */
export function TransactionMetaItem({ separated = false, className, children }: Readonly<{
  separated?: boolean;
  className?: string;
  children: ReactNode;
}>) {
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5', className)}>
      {separated && <span className="hidden text-text-tertiary sm:inline">·</span>}
      {children}
    </span>
  );
}

/** The category's dot and name — the first item of every line's meta. */
export function TransactionCategoryLabel({ meta }: Readonly<{ meta: CategoryMeta }>) {
  // style.dot keeps the TRUE category hue in light and only lifts it toward
  // white in dark (--category-dot-lift), so a mid-dark hue doesn't sink into the
  // near-black row. Sanitized CSS-var color-mix — re-resolves on a live theme flip.
  const { dot } = categoryChipStyleFromMeta(meta);
  return (
    <>
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: dot }} aria-hidden />
      {/* 3rd-level leaves show their breadcrumb when the index knows it. */}
      <span className="truncate">{meta.pathLabel ?? meta.label}</span>
    </>
  );
}

/**
 * The line's amount. `signed` is the cash-flow reading (green "+" in, red out, `muted` for flow-neutral rows);
 * `neutral` is for lists of one kind of amount (a space's expenses), plain and unsigned.
 */
export function TransactionAmount({ value, currency = 'BRL', tone = 'signed', muted = false, sub, className }: Readonly<{
  value: number;
  currency?: string;
  tone?: 'signed' | 'neutral';
  muted?: boolean;
  /** A second, smaller line: the amount in the account's currency, the purchase's total, … */
  sub?: ReactNode;
  /** A fixed width, when the line has an aside column that must stay aligned across rows. */
  className?: string;
}>) {
  const formatCurrency = useFormatCurrency();
  const signed = tone === 'signed';
  return (
    <div className={cn('shrink-0 text-right', className)}>
      <p className={cn('font-mono text-sm tabular-nums', amountColor(value, tone, muted))}>
        {signed && value >= 0 ? '+' : ''}
        {formatCurrency(value, { currency })}
      </p>
      {sub && <p className="mt-0.5 font-mono text-3xs text-text-tertiary tabular-nums">{sub}</p>}
    </div>
  );
}
